import { useCallback, useEffect, useRef } from 'react';
import { DOPPLER_CONFIG, DOPPLER_RADAR_LAYER_OPTIONS } from '../config/dopplerRadar.config.js';
import {
  createDopplerRadarTileLayer,
  retryFailedDopplerTiles,
} from '../utils/dopplerLayer.utils.js';
import { getMapTileUrl } from '../utils/dopplerTile.utils.js';
import useDopplerTileSource from './useDopplerTileSource.js';

export default function useDopplerLayerCollection({
  baseTime,
  buffer,
  clearViewTimeouts,
  displayedFrameIndex,
  frames,
  frameTransition,
  isMapReady,
  layersRef,
  map,
}) {
  const activeCollectionRef = useRef(null);
  const retryTimeoutsRef = useRef({});
  const startupSyncAnimationFrameRef = useRef(null);
  // When the collection is rebuilt (the 10-minute time anchor, a switch to the proxy), the old
  // visible frame stays on top until the new displayed frame has loaded, so the radar does not go
  // blank while twelve new frames load.
  const retiringLayerRef = useRef(null);
  const retiringLayerTimeoutRef = useRef(null);

  const {
    clearLayerError,
    clearLayerTracking,
    markLayerError,
    markLayerLoaded,
    markLayerLoading,
    markLayerNotLoading,
    prepareFrame,
    resetBufferState,
    syncUsableFramesFromLayers,
  } = buffer;
  const {
    applySettledFrameState,
    prepareFrameCollection,
    stopTransition,
    syncVisibleLayerLoadingState,
  } = frameTransition;

  const clearRetryTimeouts = useCallback(() => {
    Object.values(retryTimeoutsRef.current).forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    retryTimeoutsRef.current = {};
  }, []);

  const cancelStartupSync = useCallback(() => {
    const animationFrameId = startupSyncAnimationFrameRef.current;
    if (animationFrameId !== null) {
      window.cancelAnimationFrame(animationFrameId);
      startupSyncAnimationFrameRef.current = null;
    }
  }, []);

  const removeRetiringLayer = useCallback(() => {
    if (retiringLayerTimeoutRef.current !== null) {
      window.clearTimeout(retiringLayerTimeoutRef.current);
      retiringLayerTimeoutRef.current = null;
    }
    retiringLayerRef.current?.remove();
    retiringLayerRef.current = null;
  }, []);

  const retireLayer = useCallback(
    (layer) => {
      layer.setZIndex(DOPPLER_CONFIG.retiringZIndex);
      retiringLayerRef.current = layer;
      retiringLayerTimeoutRef.current = window.setTimeout(
        removeRetiringLayer,
        DOPPLER_CONFIG.retiringLayerTimeoutMs,
      );
    },
    [removeRetiringLayer],
  );

  const {
    tileSourceMode,
    resolvedTileSourceMode,
    resetDirectTileFailures,
    markDirectFrameRecovered,
    maybeSwitchToProxy,
  } = useDopplerTileSource({ onBeforeSourceChange: clearRetryTimeouts });

  useEffect(() => {
    if (!isMapReady || !map || !baseTime || !frames.length) return undefined;

    clearRetryTimeouts();
    cancelStartupSync();
    clearViewTimeouts();
    prepareFrameCollection(displayedFrameIndex);
    resetDirectTileFailures();
    resetBufferState();
    const collectionId = Symbol('doppler-layer-collection');
    activeCollectionRef.current = collectionId;

    const radarPane =
      map.getPane(DOPPLER_CONFIG.paneName) ?? map.createPane(DOPPLER_CONFIG.paneName);
    radarPane.style.zIndex = String(DOPPLER_CONFIG.baseZIndex);
    radarPane.style.pointerEvents = 'none';

    const layerEntries = frames.map((frame, frameIndex) => {
      prepareFrame(frameIndex);

      const layer = createDopplerRadarTileLayer(
        getMapTileUrl(baseTime, frame, undefined, { tileSourceMode }),
        DOPPLER_RADAR_LAYER_OPTIONS,
      );

      const handleLoading = () => {
        markLayerLoading(frameIndex);
        syncVisibleLayerLoadingState();
      };

      const handleLoad = () => {
        if (markLayerLoaded(frameIndex)) {
          markDirectFrameRecovered(frameIndex);
        }
        syncVisibleLayerLoadingState();
      };

      const failedTiles = new Set();

      const handleTileError = ({ tile, coords }) => {
        markLayerError(frameIndex);

        if (maybeSwitchToProxy(frameIndex)) {
          markLayerNotLoading(frameIndex);
          syncVisibleLayerLoadingState();
          return;
        }

        failedTiles.add({ tile, coords });

        const existingRetryTimeoutId = retryTimeoutsRef.current[frameIndex];
        if (existingRetryTimeoutId !== undefined) {
          window.clearTimeout(existingRetryTimeoutId);
        }

        retryTimeoutsRef.current[frameIndex] = window.setTimeout(() => {
          delete retryTimeoutsRef.current[frameIndex];
          const tilesToRetry = [...failedTiles];
          failedTiles.clear();
          clearLayerError(frameIndex);
          retryFailedDopplerTiles(layer, tilesToRetry);
        }, DOPPLER_CONFIG.retryDelayMs);
      };

      layer.on('loading', handleLoading);
      layer.on('load', handleLoad);
      layer.on('tileerror', handleTileError);

      layer.addTo(map);
      return {
        frameIndex,
        layer,
        eventHandlers: { handleLoad, handleLoading, handleTileError },
      };
    });

    layersRef.current = layerEntries;
    applySettledFrameState(displayedFrameIndex, null);
    startupSyncAnimationFrameRef.current = window.requestAnimationFrame(() => {
      if (activeCollectionRef.current !== collectionId || layersRef.current !== layerEntries) {
        return;
      }

      startupSyncAnimationFrameRef.current = null;
      syncUsableFramesFromLayers(layerEntries);
      syncVisibleLayerLoadingState();
    });

    return () => {
      if (activeCollectionRef.current === collectionId) {
        activeCollectionRef.current = null;
      }
      cancelStartupSync();
      stopTransition();
      clearRetryTimeouts();
      clearViewTimeouts();
      resetDirectTileFailures();
      clearLayerTracking();
      // Keep the frame on screen unless an older one is still waiting: a collection that never
      // became ready has nothing better to show.
      const visibleLayer = retiringLayerRef.current
        ? null
        : layerEntries.find(({ layer }) => layer.options.zIndex === DOPPLER_CONFIG.currentZIndex)
            ?.layer;
      layerEntries.forEach(({ eventHandlers, layer }) => {
        layer.off('loading', eventHandlers.handleLoading);
        layer.off('load', eventHandlers.handleLoad);
        layer.off('tileerror', eventHandlers.handleTileError);
        if (layer === visibleLayer) {
          retireLayer(layer);
        } else {
          layer.remove();
        }
      });
      layersRef.current = [];
    };
  }, [
    applySettledFrameState,
    baseTime,
    cancelStartupSync,
    clearLayerError,
    clearLayerTracking,
    clearRetryTimeouts,
    clearViewTimeouts,
    frames,
    isMapReady,
    layersRef,
    map,
    markDirectFrameRecovered,
    markLayerError,
    markLayerLoaded,
    markLayerLoading,
    markLayerNotLoading,
    maybeSwitchToProxy,
    prepareFrame,
    prepareFrameCollection,
    resetBufferState,
    resetDirectTileFailures,
    retireLayer,
    stopTransition,
    syncVisibleLayerLoadingState,
    syncUsableFramesFromLayers,
    tileSourceMode,
  ]);

  useEffect(() => {
    if (buffer.isPlaybackReady) {
      removeRetiringLayer();
    }
  }, [buffer.isPlaybackReady, removeRetiringLayer]);

  useEffect(() => removeRetiringLayer, [removeRetiringLayer]);

  return { resolvedTileSourceMode };
}
